import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { T, glass } from '../pages/shared'

const STEPS = [
  {
    title: 'Welcome to DataSentinel',
    body: 'Your end-to-end CO₂ sequestration data quality platform. 28 DQA rules, 8 quality dimensions, AI-powered corrections, and an immutable audit trail — all in one place. This tour takes about 3 minutes.',
    icon: '◈',
    route: '/dashboard',
    tag: null,
  },
  {
    title: 'Dashboard',
    body: 'Your live command centre. The readiness score gauge, 7-day trend chart, dimension score bars, and violation counts update after every run. A gate banner at the top shows PASS or FAIL at a glance.',
    icon: '⌂',
    route: '/dashboard',
    tag: 'OVERVIEW',
  },
  {
    title: 'Upload a Dataset',
    body: 'Go to Datasets → select a project → drag-and-drop or click Upload. CSV and Excel files are supported. STR1 expects 60 rows × 16 columns at 2-min frequency. STR2 expects 60 rows × 13 columns at 1-min frequency.',
    icon: '▤',
    route: '/datasets',
    tag: 'STEP 1',
  },
  {
    title: 'Rule Manager — Load Your Rules',
    body: 'Select your project and click ⚙ Seed CO₂ Rules to load all 28 rules instantly. Then use the Edit button on any rule to adjust its sigma threshold, bounds, weights, or hard-gate flag. The JSON parameters editor validates before saving.',
    icon: '⚙',
    route: '/rules',
    tag: 'STEP 2',
  },
  {
    title: 'Run DQA',
    body: 'Select project and dataset, then click ▷ Execute DQA Run. Results stream live — watch dimension scores populate in real time. The run completes in under 2 seconds for a 60-row batch.',
    icon: '▷',
    route: '/run',
    tag: 'STEP 3',
  },
  {
    title: 'Violation Browser',
    body: 'Every rule that fires creates a violation. Filter by severity, dimension or status. Expand any row to see the exact affected rows and violation JSON detail. Select multiple violations and click Batch Correct to send them to the Workbench.',
    icon: '⚑',
    route: '/violations',
    tag: 'STEP 4',
  },
  {
    title: 'Correction Workbench',
    body: 'Click ⚙ Generate Suggestions after selecting a completed run. Each suggestion shows the original value, the corrected value, a confidence bar, and the strategy used. You can Approve, Reject, or Override with your own value. Once approved, click ▷ Apply All to Production.',
    icon: '⊿',
    route: '/corrections',
    tag: 'STEP 5',
  },
  {
    title: 'Correction Manager',
    body: 'Correction Manager (at /rule-studio) pairs each detection rule with a correction strategy side-by-side. Detection rules appear on the left (orange toggles), correction strategies on the right (green toggles). IDs follow a pattern: A-01 detection pairs with AC-01 correction. Three ways to build pairs: 📚 Standard Library (10 pre-built CO₂ pairs), ✦ AI Generate (describe the problem in English → AI writes both sides), or ✎ Build Manually. Click ⚡ Apply Default Corrections to instantly pair all 28 seeded rules with their default corrections in one click.',
    icon: '≡',
    route: '/rule-studio',
    tag: 'CORRECTION',
  },
  {
    title: 'AI Correction Engine — XGBoost + SHAP',
    body: 'Every approved correction feeds the training store with a real 20-feature vector (lags, rolling stats, z-score, IQR deviation, rule flags, severity). Once ≥50 corrections accumulate, the XGBoost strategy classifier activates — predicting interpolation, forward-fill, exclusion, or substitution. SHAP TreeExplainer shows the top drivers for each prediction. The model is persisted to S3 and reloaded at inference time with no restart needed.',
    icon: '◈',
    route: '/corrections',
    tag: 'STEP 6',
  },
  {
    title: 'Correction Manager — Paired Rules',
    body: 'Each pair has: a detection toggle (orange) that controls whether the rule fires during DQA, and a correction toggle (green) that controls whether the correction auto-applies. Per-pair buttons: Edit (opens a modal with Logic section — Auto template, AI generate, or Manual JSON modes for both detection params and correction logic), Test, and Delete pair. The workflow is: Rule Manager (seed rules) → Correction Manager (pair + tune) → Run DQA.',
    icon: '⚙',
    route: '/rule-studio',
    tag: 'STEP 7',
  },
  {
    title: 'Automation — Schedule Pipeline',
    body: 'The AUTOMATION nav section (after Anomaly Detection) lets you schedule recurring DQA and anomaly runs. Schedule type: DQA Only / Anomaly Detection Only / DQA + Anomaly (both). Multi-project: one schedule can run against multiple projects simultaneously. Per-project data sources: Manual Dataset / Local Folder (Browse button) / SharePoint (folder URL + Graph API token) / S3 Bucket (bucket + prefix). Full pipeline: Pull file → Run DQA → Auto-apply corrections (with confidence threshold) → Generate corrected file + correction_report.csv → Push to /corrected subfolder → Email gate-fail list. Click Run Now for immediate execution.',
    icon: '⏱',
    route: '/schedules',
    tag: 'AUTOMATION',
  },
  {
    title: 'Dataset Connectors',
    body: 'The Datasets page now has a "+ Connect Source ▾" dropdown with four options: 📁 Local CSV/Excel (existing upload), 🔗 REST API (existing connector), ☁️ SharePoint (new — enter folder URL + Microsoft Graph API token → browse files → one-click import), 🪣 S3 Bucket (new — enter bucket name + prefix + optional credentials or use task role → browse → import). Connected sources appear alongside manually uploaded datasets and can be used in schedules.',
    icon: '▤',
    route: '/datasets',
    tag: 'DATASETS',
  },
  {
    title: 'ML Model Hub',
    body: 'Intelligence → ML Model Hub shows all four live ML models: DQA XGBoost (correction strategy), Anomaly XGBoost (TP/FP calibration), Isolation Forest (persisted 90-day global model), and Ensemble Weights (per-model accuracy). Each model shows training metrics, sample count, trained date, and S3 + memory cache state. Admin users can trigger immediate retraining — or wait for the automatic Celery beat schedule at 02:00 and 02:30 UTC nightly.',
    icon: '🧠',
    route: '/model-hub',
    tag: 'INTELLIGENCE',
  },
  {
    title: 'Trend Monitor',
    body: 'The Trend Monitor shows 30-day historical readiness score trends across all 8 quality dimensions — Completeness, Integrity, Timeliness, Uniqueness, Accuracy, Consistency, Relevance, Readiness. Track improvement over time and demonstrate continuous quality improvement to carbon registries.',
    icon: '📈',
    route: '/trends',
    tag: 'INTELLIGENCE',
  },
  {
    title: 'Audit Trail',
    body: 'Every action — runs, approvals, rejections, corrections applied, logins — is logged in an immutable append-only record. Use the weekly activity chart and event-type filters to investigate. Export as CSV or generate a full Compliance Pack for regulatory submission.',
    icon: '☰',
    route: '/audit',
    tag: 'COMPLIANCE',
  },
  {
    title: 'Reports & Compliance',
    body: 'The Reports page exports corrected datasets as CSV, generates compliance certificates, and downloads Registry V&V PDF reports for verified carbon projects. All outputs are audit-ready and registry-compatible with Puro.Earth, Isometric, and Verra.',
    icon: '📄',
    route: '/reports',
    tag: 'COMPLIANCE',
  },
  {
    title: 'User Management & Settings',
    body: 'Under Admin: invite users with Super Admin / Admin / Engineer / Analyst / Viewer roles. In Settings configure DQA thresholds, SLA limits, API keys, and security options. Each role has a defined permission set shown in the Security tab.',
    icon: '⊙',
    route: '/users',
    tag: 'ADMIN',
  },
  {
    title: 'Alerts & Webhook Configuration',
    body: 'Settings → Alerts tab: configure email alerts for gate failures, plus Slack and Microsoft Teams webhook URLs per project. Click Test to fire a sample message instantly. Alerts fire automatically whenever a DQA run gate fails — no manual follow-up needed.',
    icon: '🔔',
    route: '/settings',
    tag: 'ALERTS',
  },
  {
    title: 'Violation SLA & Comments',
    body: 'Expand any violation row to assign it to a team member, set an SLA deadline (4 h / 8 h / 24 h / 48 h), and thread a comment discussion. Overdue violations are highlighted with a red OVERDUE badge. Dimension sparklines in the expanded row show how that metric has trended across the last 20 runs.',
    icon: '⏱',
    route: '/violations',
    tag: 'OPERATIONS',
  },
  {
    title: 'Pre-Submission Compliance Checklist',
    body: 'On the Run DQA page, click ✓ Run Checklist on any completed run to see 5 gate checks: gate passed, all violations resolved, corrections applied, AI narrative generated, and V&V verified. A green "Ready to Submit" badge means all five are clear. You can also track upcoming registry deadlines via the Submission Windows countdown on the Dashboard.',
    icon: '✅',
    route: '/run',
    tag: 'COMPLIANCE',
  },
  {
    title: 'Instrument Calibration Log',
    body: 'Settings → Calibration tab: track the calibration status of every field sensor. Add each instrument with its last and next calibration dates, certification number, and location. The table flags overdue instruments in red and instruments due within 30 days in amber — keeping your measurement traceability audit-ready.',
    icon: '🔧',
    route: '/settings',
    tag: 'CALIBRATION',
  },
  {
    title: 'System Status & Retention',
    body: 'System Status (sidebar → System Status) shows live health of the backend, database, S3 storage, and LLM API — with entity counts and the last ingest timestamp. The LLM card expands to show your full AI config: provider, masked API key, endpoint, deployment, and API version (useful for verifying Azure OpenAI is wired up correctly). In Settings → Retention, configure how long run records and violations are retained before auto-archival.',
    icon: '◉',
    route: '/status',
    tag: 'OPERATIONS',
  },
  {
    title: 'User Manual',
    body: 'Full in-depth documentation is available in the User Manual page — 17 sections covering the platform workflow, all 28 rule definitions, AI model architecture, alerts, compliance checklist, calibration, retention, API reference with cURL examples, and a FAQ. Access it anytime from the sidebar.',
    icon: '☰',
    route: '/manual',
    tag: 'HELP',
  },
  {
    title: 'Anomaly Detection',
    body: 'The Anomaly Detection module sits between DQA and V&V. After running detection, click Analyse on any anomaly to enter the six-step GenAI workflow: detection results → AI recommendations → Gen-AI suggestions (accept or reject each) → your expert recommendation → data correction stored as ML training data.',
    icon: '◎',
    route: '/anomaly',
    tag: 'ANOMALY DETECTION',
  },
  {
    title: 'Three-Model Ensemble + XGBoost Calibration',
    body: 'Three models vote on each reading: Heuristic (threshold bounds), Statistical (IQR+Z-score), and Isolation Forest (persisted global model, refit nightly on 90-day data). When 2+ agree, the ensemble flags an anomaly. A fourth layer — Anomaly XGBoost — then produces a calibrated true-anomaly probability with SHAP feature attribution. Ensemble weights update automatically each night based on your TP/FP feedback, so the models improve as you use the platform. The GenAI layer calls Azure OpenAI or Claude for root cause hypotheses and operational recommendations.',
    icon: '🤖',
    route: '/anomaly',
    tag: 'ANOMALY DETECTION',
  },
  {
    title: 'Knowledge Base',
    body: 'The Knowledge Base is your operational intelligence store — it feeds every GenAI recommendation. Add entries from incident reports, operating manuals, and lessons learned. Every entry you save from an anomaly analysis automatically enriches future recommendations with a "From your KB" confidence badge.',
    icon: '📚',
    route: '/knowledge-base',
    tag: 'ANOMALY DETECTION',
  },
  {
    title: 'V&V Portfolio Dashboard',
    body: 'The V&V Portfolio view shows all verification projects as visual cards — each card displays a completeness ring (how many required documents are uploaded), expiry warnings, submission deadline countdown, and overall status at a glance. Click any card to enter the project.',
    icon: '⬡',
    route: '/vv',
    tag: 'REGISTRY V&V',
  },
  {
    title: 'Creating a Verification Project',
    body: 'Click New Verification. Select registry (e.g. Puro.Earth) and methodology (e.g. CCS / CO₂ Mineralisation or Biochar). The platform loads the correct checkpoint set automatically — 23 checkpoints for Puro.Earth CCS, modelled on real submissions.',
    icon: '📋',
    route: '/vv',
    tag: 'REGISTRY V&V',
  },
  {
    title: 'Documentation Completeness Scorecard',
    body: 'At the top of the Documents tab, a live scorecard shows your completeness percentage — green (100%), amber (60-99%), red (<60%). Missing document types are shown as red chips. Required documents are driven automatically by the Protocol Manager — adding or editing checkpoints in a registry protocol updates the required document list with no code changes. The scorecard refreshes every 15 seconds after an upload.',
    icon: '📊',
    route: '/vv',
    tag: 'REGISTRY V&V',
  },
  {
    title: 'Smart Folder Connector — 3 Sources',
    body: 'The Folder Connector sits at the top of the Documents tab — click the 📁 header row to collapse it when not in use, and it auto-collapses after a successful import. A slim ▼ Open pill re-opens it instantly. Supports PDF, XLSX, DOCX, PPTX, CSV, JSON, TXT, and HTML. AI classifies every file automatically — review assignments, adjust any type, then click Import to bulk-ingest. After import, a persistent Import Summary panel shows the full result with file-type breakdown. Use ▲ Hide / ▼ Show to collapse or expand the summary — it never disappears.',
    icon: '📁',
    route: '/vv',
    tag: 'REGISTRY V&V',
  },
  {
    title: 'Document Expiry Tracking',
    body: 'Set an expiry date on any document using the 📅 Expiry button on the document row. Documents within 60 days of expiry show a 🟡 warning badge; expired documents show 🔴. The Portfolio Dashboard counts expiring documents per project, so no NOC or company registration slips past its validity date.',
    icon: '⏰',
    route: '/vv',
    tag: 'REGISTRY V&V',
  },
  {
    title: 'AI Content Validation',
    body: 'Click ✦ Validate on any document row. The AI reads the extracted text and runs a type-specific checklist — e.g. for an LCA Report it checks: system boundary defined, functional unit stated, emission factors cited, net removal calculated. Each check is marked pass/partial/fail/unknown with a score from 0-100%. The score persists on the document row.',
    icon: '✦',
    route: '/vv',
    tag: 'REGISTRY V&V',
  },
  {
    title: 'Cross-Document Consistency Checker',
    body: 'The Consistency Check tab lets you run a full AI scan across all uploaded documents. The AI looks for conflicting numbers (e.g. CO₂ volumes in the offtake agreement vs. the LCA spreadsheet), mismatched dates, and contradictory claims. Discrepancies are shown with severity (high / medium / low) and a recommended action.',
    icon: '🔍',
    route: '/vv',
    tag: 'REGISTRY V&V',
  },
  {
    title: 'Document Review Workflow',
    body: 'Click 💬 Review on any document to open the review drawer. Set the status (Draft → Under Review → Approved / Rejected), add review notes, and post threaded comments. Approved documents show a sign-off timestamp with the reviewer\'s name — creating a complete review trail for each document.',
    icon: '💬',
    route: '/vv',
    tag: 'REGISTRY V&V',
  },
  {
    title: 'Monitoring Data Charts',
    body: 'For CSV and Excel documents (temperature time-series, monitoring data, production logs), click 📈 Chart. DataSentinel auto-detects the timestamp column and numeric series, and renders a live Recharts line chart. Up to 8 series are shown simultaneously — great for reviewing pyrolysis temperature profiles or injection pressure logs.',
    icon: '📈',
    route: '/vv',
    tag: 'REGISTRY V&V',
  },
  {
    title: 'Submission Package Builder',
    body: 'Once documents are uploaded and reviewed, click 📦 Build Package in the project header. DataSentinel downloads all documents from storage, renames them to registry naming conventions, generates a MANIFEST.csv with review status and sign-off details, and delivers a single ZIP file ready for registry submission. Missing documents are flagged in a separate text file inside the ZIP.',
    icon: '📦',
    route: '/vv',
    tag: 'REGISTRY V&V',
  },
  {
    title: 'Audit Log',
    body: 'The Audit Log tab shows a complete tamper-evident timeline of every action on the project — documents uploaded, deleted, validated, reviewed, consistency checks run, and comments posted. Every entry records the actor name and timestamp. Documents are soft-deleted (hidden, not destroyed) so the audit trail is never broken.',
    icon: '📋',
    route: '/vv',
    tag: 'REGISTRY V&V',
  },
  {
    title: 'AI Verification & Report',
    body: 'Click Run AI Verification to analyse all documents against each checkpoint. The AI reads CSVs, PDFs, and spreadsheets — extracting temperatures, net CO₂, NOC status. Review findings checkpoint by checkpoint, override with your own judgment, then Generate Report for a VERIFIED / CONDITIONAL / NOT VERIFIED outcome with an AI-written executive summary.',
    icon: '◎',
    route: '/vv',
    tag: 'REGISTRY V&V',
  },
  {
    title: 'Finding Severity Grading',
    body: 'Every checkpoint result is graded with a finding severity — critical (🔴), major (🟠), minor (🟡), observation (🔵), or none (⚪). Severity and pass/fail are independent: a checkpoint can pass with an observation, or fail critically. Verifiers can override the AI-assigned severity on the Checkpoints tab before raising CARs.',
    icon: '⚑',
    route: '/vv',
    tag: 'PHASE 1',
  },
  {
    title: 'Corrective Action Requests (CARs)',
    body: 'The CARs tab is where formal non-conformances are raised and tracked. Click + Raise CAR, choose Major NC or Minor NC severity, link to a checkpoint code, and write a description. The project developer responds in writing; the verifier then closes or re-opens. All major-NC CARs must be Closed before the registry submission is accepted.',
    icon: '📋',
    route: '/vv',
    tag: 'PHASE 1',
  },
  {
    title: 'Formal Verification Decision',
    body: 'The Decision tab is where the lead verifier records the official outcome: Approved, Conditional Approval, Rejected, or Deferred. Add a findings summary and — for conditional approvals — specific conditions the developer must fulfil. Every decision is preserved in a full history log; superseded decisions are never deleted.',
    icon: '✔',
    route: '/vv',
    tag: 'PHASE 1',
  },
  {
    title: 'Credit Quantity Calculation',
    body: 'The Report tab shows a credit quantity waterfall: Gross Removals → Buffer Pool Deduction → Net Issuable Credits. Click Compute Credit Quantity and the AI extracts figures from your monitoring documents and applies the methodology buffer rate (e.g. 20% for Puro.Earth biochar). A materiality table flags any line item above 5% of the total claim — the ISO 14064-2 threshold that requires primary evidence.',
    icon: '📊',
    route: '/vv',
    tag: 'PHASE 2',
  },
  {
    title: 'Additionality & Permanence Assessment',
    body: 'The AI Deep Analysis tab now includes two deep assessments. Additionality applies the ISO 14064-2 three-test framework: financial barrier, regulatory barrier, and common practice — returning a rating of Strong / Adequate / Weak / Insufficient Evidence. Permanence rates the long-term stability of the carbon removal, estimates reversal risk, recommends a buffer pool %, and lists ongoing monitoring requirements.',
    icon: '🔬',
    route: '/vv',
    tag: 'PHASE 3',
  },
  {
    title: 'Two-Person Rule (Countersign)',
    body: 'The Decision tab has a Countersign section below the active decision. A second, independent verifier — different from the decision recorder — enters their name and a review note, then clicks Countersign. The platform generates a SHA-256 hash over the combined record (project + decision + reviewer + timestamp) creating a cryptographically tamper-evident sign-off that meets ISO 17029 four-eyes requirements.',
    icon: '🔐',
    route: '/vv',
    tag: 'PHASE 4',
  },
  {
    title: 'Verification SLA Tracker',
    body: 'A live SLA pill in every project header shows days remaining to the submission deadline. Green = on track (>14d), Amber = warning (7–14d), Orange = at risk (1–7d), Red = overdue. Set or update the deadline via Edit Project. The Portfolio Dashboard also sorts by SLA so the most time-critical projects surface first.',
    icon: '⏱',
    route: '/vv',
    tag: 'PHASE 5',
  },
  {
    title: 'Registry Submission',
    body: 'The Registry Submit section in the Decision tab is the final step. Once a decision is Approved (and optionally countersigned), click Submit to Registry. A numbered submission record (REG-SUB-001) is created with timestamp and your name. A warning is shown if no countersign is present — you can still submit, but it is flagged in the audit trail. The full submission history is preserved per project.',
    icon: '⬡',
    route: '/vv',
    tag: 'PHASE 6',
  },
  {
    title: 'Reviewer Platform Overview',
    body: 'The Reviewer Platform is a separate platform for third-party verifiers. It runs a 7-phase workflow (R0–R6). R0 Assignment: admin receives a project from a registry via Sync Now and assigns it to a reviewer in Incoming Assignments. The reviewer sees it in My Assignments → Accepts → COI declaration → V&V project auto-created with all features enabled → "📋 Open Review Workspace →" button appears. Nav: Reviewer Dashboard → Incoming Assignments → My Assignments → CAR Management → Statements Archive → Integration Hub → Registry Connectors.',
    icon: '◎',
    route: '/reviewer',
    tag: 'REVIEWER',
  },
  {
    title: 'Registry Connectors & Integration Hub',
    body: 'Registry Connectors: configure Isometric and Puro.Earth API credentials, then click Test Connection to verify (shows latency + pending assignment count). Integration Hub: Sync Now pulls new assignments from all connected registries and populates Incoming Assignments. An event log shows every sync, assignment, and status change. Phases R1–R6 cover: Verification Plan → Document Pre-screen (AI completeness + plausibility) → Checkpoint review with AI pre-assessment + evidence linking → CAR Management (issue CARs, AI assesses responses) → Verification Opinion (credit quantity, additionality, permanence) → Statement (AI drafts → reviewer edits → sign + countersign → submit to registry).',
    icon: '⬡',
    route: '/reviewer/connectors',
    tag: 'REVIEWER',
  },
  {
    title: 'Two-Factor Authentication (MFA)',
    body: 'Settings → Security tab → Enable Two-Factor Authentication. Scan the QR code with Google Authenticator or Authy, enter your 6-digit code to confirm, and MFA activates immediately. On future logins, you\'ll be prompted for a TOTP code after your password — protecting your account even if credentials are compromised.',
    icon: '🔐',
    route: '/settings',
    tag: 'SECURITY',
  },
  {
    title: 'Platform Access Control',
    body: 'Each user now gets 4 platform toggles in User Management: DQA, ANOMALY, V&V, and REVIEWER. Admins and Super Admins always see all platforms (shown with a FULL ACCESS badge — no toggles needed). New SSO users start with all platforms disabled until an admin grants access. Users with all platforms off see a "Pending Access" page instead of the normal dashboard. Toggle access per-platform from Admin → User Management → the user row.',
    icon: '🔐',
    route: '/users',
    tag: 'ADMIN',
  },
  {
    title: 'Floating AI Chat',
    body: 'Every page now has a floating orange sparkle ✦ button fixed to the bottom-right corner. Click it to slide in the AI chat panel from the right. The assistant is context-aware — it knows which page you are on and passes that context automatically. Use the project selector in the chat header to scope questions to a specific project. Quick question chips let you start a conversation in one click; follow-up suggestion chips appear after each answer. A typing indicator shows while the AI is thinking.',
    icon: '✦',
    route: '/dashboard',
    tag: 'AI',
  },
  {
    title: 'Self-Service Password Reset',
    body: 'On the Sign-In page click "Forgot password?" and enter your email address. A secure reset link is emailed and expires in 2 hours. The system never reveals whether an email is registered — safe from enumeration attacks. Login attempts are also rate-limited to 10 per minute per IP.',
    icon: '📧',
    route: '/login',
    tag: 'SECURITY',
  },
  {
    title: 'NL Rule Builder — AI Rule Creation',
    body: 'Rule Manager → click ✨ NL Rule Builder. Describe any validation rule in plain English — e.g. "Flag methane flow below 0 or above 500 m³/h as critical". The AI generates the full rule configuration (ID, dimension, severity, parameters). Click ↗ Use This Rule to pre-fill the Create Rule form in one click.',
    icon: '✨',
    route: '/rules',
    tag: 'AI V5',
  },
  {
    title: 'Carbon Credit Impact Quantifier',
    body: 'Violations → expand any violation row → click £ Impact. DataSentinel calculates the estimated CO₂ tonnes at risk and their market value using affected rows, injection frequency, CO₂ density, severity factor, and the current spot price (default £50/tCO₂). Edit the spot price and click Recalc to run a sensitivity analysis in real time.',
    icon: '💷',
    route: '/violations',
    tag: 'AI V5',
  },
  {
    title: 'Predictive Quality Forecasting',
    body: 'Trend Monitor → toggle "Show Forecast". The engine uses linear regression across your last 30 runs to project the next 5 readiness scores per dimension. The R² confidence score tells you how reliable the forecast is — R² > 0.80 means a strong trend. Use this to predict quality gate risks before the next submission deadline.',
    icon: '📈',
    route: '/trends',
    tag: 'AI V5',
  },
  {
    title: "You're Ready to Go",
    body: 'DQA Platform: Upload → Seed Rules → Correction Manager (pair rules) → Run DQA → Violations → Workbench → Apply Corrections → Reports. Anomaly Detection Platform: Detect → Label → ML trains nightly. Automation: schedule DQA/Anomaly/Both across projects with SharePoint/S3/Local sources and auto-correct pipelines. Reviewer Platform: Sync from registry → Assign → Accept → COI → R1–R6 workflow → Statement → Submit. V&V Platform: Upload Docs → AI Verify → CARs → Decision → Credit Quantity → Additionality + Permanence → Countersign → Submit. Access Control: DQA / ANOMALY / V&V / REVIEWER toggles per user. AI Chat: ✦ button on every page. ML: four models retrain at 02:00/02:30 UTC. Security: MFA in Settings.',
    icon: '✔',
    route: '/dashboard',
    tag: null,
  },
]

interface TourProps { onComplete: () => void }

export default function Tour({ onComplete }: TourProps) {
  const [step, setStep] = useState(0)
  const navigate = useNavigate()
  const current = STEPS[step]
  const isLast = step === STEPS.length - 1
  const total = STEPS.length

  useEffect(() => { navigate(current.route) }, [step])

  const next = () => { if (!isLast) { setStep(s => s + 1) } else { finish() } }
  const prev = () => { if (step > 0) setStep(s => s - 1) }
  const finish = () => { localStorage.setItem('ds_tour_done', '1'); onComplete() }
  const handleBackdrop = (e: React.MouseEvent) => { if (e.target === e.currentTarget) finish() }

  return (
    <div onClick={handleBackdrop} style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      paddingBottom: 36, backdropFilter: 'blur(10px) saturate(1.1)', WebkitBackdropFilter: 'blur(10px) saturate(1.1)',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 540,
        ...glass,
        borderRadius: 16, padding: '28px 32px',
        background: 'var(--glass-modal-bg)',
        boxShadow: 'var(--glass-card-outer), var(--glass-card-inset), 0 32px 64px rgba(0,0,0,0.45)',
        animation: 'fadeUp 0.3s cubic-bezier(0.16,1,0.3,1)',
      }}>

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: 5, marginBottom: 20 }}>
          {STEPS.map((_, i) => (
            <div key={i} onClick={() => setStep(i)} style={{
              width: i === step ? 22 : 7, height: 7, borderRadius: 4,
              background: i === step ? T.canyon : i < step ? `${T.canyon}55` : 'rgba(255,255,255,0.12)',
              cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.16,1,0.3,1)',
              flexShrink: 0,
            }} />
          ))}
        </div>

        {/* Tag + counter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          {current.tag && (
            <span style={{ fontSize: 9, fontWeight: 700, fontFamily: T.fontDisplay, letterSpacing: 1.2,
              textTransform: 'uppercase', color: T.canyon, background: T.canyonDim,
              border: `1px solid ${T.canyon}35`, padding: '2px 8px', borderRadius: 4 }}>
              {current.tag}
            </span>
          )}
          <span style={{ fontSize: 11, color: T.textMuted, fontFamily: T.fontDisplay, fontWeight: 600, letterSpacing: 0.5 }}>
            {step + 1} / {total}
          </span>
        </div>

        {/* Icon + Title */}
        <div style={{ fontSize: 30, marginBottom: 10, lineHeight: 1 }}>{current.icon}</div>
        <h2 style={{ fontFamily: T.fontDisplay, fontSize: 21, fontWeight: 800, color: T.textPrimary,
          letterSpacing: -0.4, marginBottom: 12, lineHeight: 1.2 }}>
          {current.title}
        </h2>

        {/* Body */}
        <p style={{ fontSize: 14, color: T.textSecondary, lineHeight: 1.7, marginBottom: 26 }}>
          {current.body}
        </p>

        {/* Progress bar */}
        <div style={{ height: 3, background: T.surface4, borderRadius: 2, marginBottom: 22, overflow: 'hidden' }}>
          <div style={{ width: `${((step + 1) / total) * 100}%`, height: '100%',
            background: `linear-gradient(90deg, ${T.canyon}, ${T.olivine})`,
            borderRadius: 2, transition: 'width 0.4s cubic-bezier(0.16,1,0.3,1)' }} />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {step > 0 && (
            <button onClick={prev} style={{ padding: '9px 18px', borderRadius: T.rSm,
              border: `1px solid ${T.border2}`, background: T.surface3,
              color: T.textSecondary, cursor: 'pointer', fontSize: 13, fontFamily: T.fontBody }}>
              ← Back
            </button>
          )}
          <button onClick={next} style={{ padding: '9px 26px', borderRadius: T.rSm,
            border: 'none', background: `linear-gradient(135deg,${T.canyon},${T.canyon80})`,
            color: T.offWhite, cursor: 'pointer', fontSize: 13, fontWeight: 700,
            fontFamily: T.fontBody, boxShadow: `0 4px 14px ${T.canyonGlow}` }}>
            {isLast ? '✔ Get Started' : 'Next →'}
          </button>
          <button onClick={finish} style={{ marginLeft: 'auto', background: 'none', border: 'none',
            color: T.textMuted, cursor: 'pointer', fontSize: 12, fontFamily: T.fontBody, padding: '4px 8px' }}>
            Skip tour
          </button>
        </div>
      </div>
    </div>
  )
}
